import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { AdminAPI } from '../../utils/adminApi';
import { getToken } from '../../utils/auth';
import { FaUser, FaEnvelope, FaCalendar, FaTrash, FaEdit, FaCommentSlash, FaBan, FaBook, FaPuzzlePiece, FaUserShield } from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import InputDialog from './InputDialog';
import RoleSelectDialog from './RoleSelectDialog';

interface UserDetailModalProps {
    selectedUser: any;
    userContent: any;
    setSelectedUser: (user: any) => void;
    setUserContent: (content: any) => void;
    loadData: () => void;
    handleUpdateRole: (userId: string, newRole: string) => Promise<void>;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
    selectedUser,
    userContent,
    setSelectedUser,
    setUserContent,
    loadData,
    handleUpdateRole
}) => {
    const [confirmConfig, setConfirmConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        action: () => Promise<void>;
        isDestructive: boolean;
    }>({
        isOpen: false,
        title: '',
        message: '',
        action: async () => { },
        isDestructive: false
    });

    const [inputConfig, setInputConfig] = useState<{
        isOpen: boolean;
        title: string;
        defaultValue: string;
        label: string;
        action: (value: string) => Promise<void>;
    }>({
        isOpen: false,
        title: '',
        defaultValue: '',
        label: '',
        action: async () => { }
    });

    const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
    const [isSecondaryRoleDialogOpen, setIsSecondaryRoleDialogOpen] = useState(false);

    const handleRenameUser = () => {
        setInputConfig({
            isOpen: true,
            title: 'Đổi Tên Người Dùng',
            label: 'Nhập tên hiển thị mới:',
            defaultValue: selectedUser.name || '',
            action: async (newName) => {
                if (!newName || newName.trim() === '') return;
                const token = getToken();
                if (!token) return;
                try {
                    await AdminAPI.updateUserName(selectedUser.id, newName.trim(), token);
                    toast.success('Đã đổi tên thành công!');
                    setSelectedUser({ ...selectedUser, name: newName.trim() });
                    loadData();
                } catch (error) {
                    console.error('Error renaming user:', error);
                    toast.error('Không thể đổi tên');
                }
            }
        });
    };

    const handleDeleteMessages = () => {
        setConfirmConfig({
            isOpen: true,
            title: 'Xóa Tin Nhắn?',
            message: `Bạn có chắc chắn muốn xóa TẤT CẢ tin nhắn của user ${selectedUser.email}? Hành động này không thể hoàn tác.`,
            isDestructive: true,
            action: async () => {
                const token = getToken();
                if (!token) return;
                try {
                    const result = await AdminAPI.deleteUserMessages(selectedUser.id, token);
                    toast.success(`Đã xóa ${result.deletedCount} tin nhắn!`);
                } catch (error) {
                    console.error('Error deleting messages:', error);
                    toast.error('Không thể xóa tin nhắn');
                }
            }
        });
    };

    const handleDeleteContent = () => {
        setConfirmConfig({
            isOpen: true,
            title: 'Xóa Nội Dung?',
            message: `Bạn có chắc chắn muốn xóa TẤT CẢ Classes và Quizzes của user ${selectedUser.email}?`,
            isDestructive: true,
            action: async () => {
                const token = getToken();
                if (!token) return;
                try {
                    const result = await AdminAPI.deleteUserContent(selectedUser.id, token);
                    toast.success(`Đã xóa ${result.deletedClasses} classes và ${result.deletedQuizzes} quizzes!`);
                } catch (error) {
                    console.error('Error deleting content:', error);
                    toast.error('Không thể xóa content');
                }
            }
        });
    };

    const handleDeleteAccount = () => {
        setConfirmConfig({
            isOpen: true,
            title: 'Xóa Tài Khoản?',
            message: `CẢNH BÁO: Bạn sắp xóa vĩnh viễn tài khoản ${selectedUser.email}. Tất cả dữ liệu liên quan sẽ bị mất. Bạn có chắc chắn không?`,
            isDestructive: true,
            action: async () => {
                const token = getToken();
                if (token) {
                    try {
                        await AdminAPI.deleteUser(selectedUser.id, token);
                        toast.success('Đã xóa tài khoản!');
                        setSelectedUser(null);
                        loadData();
                    } catch (e) {
                        toast.error('Lỗi khi xóa tài khoản!');
                    }
                }
            }
        });
    };

    const [roleToChange, setRoleToChange] = useState<any>(null);

    const roles = [
        { id: 'admin', label: 'Quản trị viên', description: 'Quản trị viên hệ thống', color: 'purple', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
        { id: 'lop_truong', label: 'Lớp trưởng', description: 'Quyền cao nhất trong lớp', color: 'blue', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
        { id: 'bi_thu', label: 'Bí thư', description: 'Quản lý tin tức, thông báo', color: 'red', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
        { id: 'pho_bi_thu', label: 'Phó bí thư', description: 'Hỗ trợ bí thư', color: 'orange', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
        { id: 'lop_pho_hoc_tap', label: 'Lớp phó học tập', description: 'Quản lý quiz, tài liệu', color: 'indigo', bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300' },
        { id: 'lop_pho_doi_song', label: 'Lớp phó đời sống', description: 'Quản lý gallery, sự kiện', color: 'teal', bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-700 dark:text-teal-300' },
        { id: 'thu_quy', label: 'Thủ quỹ', description: 'Quản lý thông tin tài chính', color: 'yellow', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300' },
        { id: 'uy_vien', label: 'Ủy viên', description: 'Ủy viên ban chấp hành', color: 'emerald', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
        { id: 'thanh_vien', label: 'Thành viên', description: 'Quyền hạn cơ bản', color: 'green', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
    ];

    const currentRole = roles.find(r => r.id === selectedUser.role) || roles[0];

    const handleRoleSelect = (roleId: string) => {
        if (roleId === selectedUser.role) return;
        const role = roles.find(r => r.id === roleId);
        setRoleToChange(role);
        setConfirmConfig({
            isOpen: true,
            title: 'Đổi Vai Trò?',
            message: `Bạn có chắc chắn muốn đổi vai trò của ${selectedUser.name} thành "${role?.label}"?`,
            isDestructive: roleId !== 'admin',
            action: async () => {
                await handleUpdateRole(selectedUser.id, roleId);
            }
        });
    };

    const handleSecondaryRoleSelect = async (roleId: string | null) => {
        const token = getToken();
        if (!token) return;

        try {
            await AdminAPI.updateSecondaryRole(selectedUser.id, roleId, token);
            toast.success(roleId ? 'Đã cập nhật vai trò phụ!' : 'Đã xóa vai trò phụ!');
            setSelectedUser({ ...selectedUser, secondaryRole: roleId });
            loadData();
        } catch (error) {
            console.error('Error updating secondary role:', error);
            toast.error('Không thể cập nhật vai trò phụ');
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4 animate-fadeIn">
                <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-2xl shadow-2xl w-full h-full sm:max-w-5xl sm:w-full sm:max-h-[90vh] overflow-hidden flex flex-col border-0 sm:border border-gray-100 dark:border-gray-700 animate-scaleIn">
                    {/* Header */}
                    <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-start bg-gray-50/50 dark:bg-gray-800/50">
                        <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-lg sm:text-2xl font-bold text-white shadow-lg shrink-0">
                                {selectedUser.email.substring(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                                    {selectedUser.name || 'Người dùng chưa đặt tên'}
                                </h2>
                                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mt-1 text-sm">
                                    <FaEnvelope className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                                    <span className="truncate">{selectedUser.email}</span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-sm whitespace-nowrap ${roles.find(r => r.id === selectedUser.role)?.bg || 'bg-gray-100'
                                        } ${roles.find(r => r.id === selectedUser.role)?.text || 'text-gray-700'
                                        }`}>
                                        {roles.find(r => r.id === selectedUser.role)?.label || selectedUser.role}
                                    </span>
                                    {selectedUser.secondaryRole && (
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-sm whitespace-nowrap ${roles.find(r => r.id === selectedUser.secondaryRole)?.bg || 'bg-gray-100'
                                            } ${roles.find(r => r.id === selectedUser.secondaryRole)?.text || 'text-gray-700'
                                            }`}>
                                            {roles.find(r => r.id === selectedUser.secondaryRole)?.label || selectedUser.secondaryRole}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => { setSelectedUser(null); setUserContent(null); }}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all shrink-0 ml-2"
                        >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
                        {/* Action Grid */}
                        <section>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 sm:mb-4">Hành Động Quản Trị</h3>
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                                <button onClick={() => setIsRoleDialogOpen(true)} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-indigo-50 dark:bg-indigo-900/10 text-indigo-700 dark:text-indigo-300 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-all group">
                                    <div className="p-2 bg-white dark:bg-indigo-900/30 rounded-lg group-hover:scale-110 transition-transform"><FaUserShield /></div>
                                    <span className="font-semibold text-xs sm:text-sm">Đổi Vai Trò</span>
                                </button>
                                <button onClick={() => setIsSecondaryRoleDialogOpen(true)} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-purple-50 dark:bg-purple-900/10 text-purple-700 dark:text-purple-300 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/20 transition-all group">
                                    <div className="p-2 bg-white dark:bg-purple-900/30 rounded-lg group-hover:scale-110 transition-transform"><FaUserShield /></div>
                                    <span className="font-semibold text-xs sm:text-sm">Vai Trò Phụ</span>
                                </button>
                                <button onClick={handleRenameUser} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-300 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all group">
                                    <div className="p-2 bg-white dark:bg-blue-900/30 rounded-lg group-hover:scale-110 transition-transform"><FaEdit /></div>
                                    <span className="font-semibold text-xs sm:text-sm">Đổi Tên</span>
                                </button>
                                <button onClick={handleDeleteMessages} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-300 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-900/20 transition-all group">
                                    <div className="p-2 bg-white dark:bg-orange-900/30 rounded-lg group-hover:scale-110 transition-transform"><FaCommentSlash /></div>
                                    <span className="font-semibold text-xs sm:text-sm">Xóa Tin Nhắn</span>
                                </button>
                                <button onClick={handleDeleteContent} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-300 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/20 transition-all group">
                                    <div className="p-2 bg-white dark:bg-red-900/30 rounded-lg group-hover:scale-110 transition-transform"><FaTrash /></div>
                                    <span className="font-semibold text-xs sm:text-sm">Xóa Nội Dung</span>
                                </button>
                                <button onClick={handleDeleteAccount} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all group border-2 border-transparent hover:border-red-500 hover:text-red-600">
                                    <div className="p-2 bg-white dark:bg-gray-800 rounded-lg group-hover:scale-110 transition-transform"><FaBan /></div>
                                    <span className="font-semibold text-xs sm:text-sm">Xóa Tài Khoản</span>
                                </button>
                            </div>
                        </section>

                        {/* User Content */}
                        {userContent && (
                            <section className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                {/* Classes */}
                                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 sm:p-4 border border-gray-100 dark:border-gray-700">
                                    <h3 className="flex items-center gap-2 font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 text-sm sm:text-base">
                                        <span className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg"><FaBook className="w-3 h-3 sm:w-4 sm:h-4" /></span>
                                        Classes ({userContent.classes.length})
                                    </h3>
                                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                        {userContent.classes.map((cls: any) => (
                                            <div key={cls.id} className="flex justify-between items-center p-2 sm:p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                                                <span className="font-medium text-gray-700 dark:text-gray-300 truncate flex-1 mr-2 text-xs sm:text-sm">{cls.name}</span>
                                                <span className={`text-xs px-2 py-1 rounded font-bold shrink-0 ${cls.isPublic ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                                                    {cls.isPublic ? 'Public' : 'Private'}
                                                </span>
                                            </div>
                                        ))}
                                        {userContent.classes.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Trống</p>}
                                    </div>
                                </div>

                                {/* Quizzes */}
                                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 sm:p-4 border border-gray-100 dark:border-gray-700">
                                    <h3 className="flex items-center gap-2 font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 text-sm sm:text-base">
                                        <span className="p-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg"><FaPuzzlePiece className="w-3 h-3 sm:w-4 sm:h-4" /></span>
                                        Quizzes ({userContent.quizzes.length})
                                    </h3>
                                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                        {userContent.quizzes.map((quiz: any) => (
                                            <div key={quiz.id} className="flex justify-between items-center p-2 sm:p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                                                <span className="font-medium text-gray-700 dark:text-gray-300 truncate flex-1 mr-2 text-xs sm:text-sm">{quiz.title}</span>
                                                <span className={`text-xs px-2 py-1 rounded font-bold shrink-0 ${quiz.published ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                                                    {quiz.published ? 'Published' : 'Draft'}
                                                </span>
                                            </div>
                                        ))}
                                        {userContent.quizzes.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Trống</p>}
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                title={confirmConfig.title}
                message={confirmConfig.message}
                isDestructive={confirmConfig.isDestructive}
                onCancel={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={async () => {
                    setConfirmConfig({ ...confirmConfig, isOpen: false });
                    await confirmConfig.action();
                }}
            />

            <InputDialog
                isOpen={inputConfig.isOpen}
                title={inputConfig.title}
                label={inputConfig.label}
                defaultValue={inputConfig.defaultValue}
                onCancel={() => setInputConfig({ ...inputConfig, isOpen: false })}
                onConfirm={async (value) => {
                    setInputConfig({ ...inputConfig, isOpen: false });
                    await inputConfig.action(value);
                }}
            />

            {/* Secondary Role Dialog */}
            <RoleSelectDialog
                isOpen={isSecondaryRoleDialogOpen}
                currentRole={selectedUser.secondaryRole || ''}
                roles={[
                    { id: '', label: 'Không có vai trò phụ', description: 'Xóa vai trò phụ', color: 'gray', bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300' },
                    ...roles.filter(r => r.id !== selectedUser.role)
                ]}
                userName={selectedUser.name || selectedUser.email}
                onSelect={async (roleId) => {
                    setIsSecondaryRoleDialogOpen(false);
                    await handleSecondaryRoleSelect(roleId || null);
                }}
                onCancel={() => setIsSecondaryRoleDialogOpen(false)}
            />

            <RoleSelectDialog
                isOpen={isRoleDialogOpen}
                currentRole={selectedUser.role}
                roles={roles}
                userName={selectedUser.name || selectedUser.email}
                onSelect={async (roleId) => {
                    setIsRoleDialogOpen(false);
                    await handleUpdateRole(selectedUser.id, roleId);
                }}
                onCancel={() => setIsRoleDialogOpen(false)}
            />
        </>
    );
};

export default UserDetailModal;
